import type { TabViewRef } from '@myosh/odin-components';
import { Loader, TabViewPanel } from '@myosh/odin-components';
import dragula from 'dragula';
import { t } from 'i18next';
import { differenceWith, isEmpty, isEqual } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { resetChildrenPosition } from '../../helpers/reset-children-position';
import { useLayoutAdmin } from '../../hooks/useIsLayoutAdmin';
import type { Layout } from '../../redux/config/layout';
import { useDeleteLayoutMutation, useGetLayoutsQuery, useUpdateLayoutMutation } from '../../redux/config/layout';
import { useGetLayoutWidgetQuery, useUpdateLayoutWidgetDataMutation } from '../../redux/config/layout-widget';
import { useAppDispatch } from '../../redux/hooks';
import {
  selectActiveTabId,
  setActiveLayoutTabId,
  setActiveLayoutTabType,
} from '../../redux/slices/active-layout-tab-id-slice';
import { setLayoutTabs, setLayoutWidgets } from '../../redux/slices/layout-tabs-slice';
import type { LayoutWeight } from '../../redux/slices/new-tab-order';
import { changedLayoutsWeight, discardNewOrder } from '../../redux/slices/new-tab-order';
import {
  clearStagedWidgetDimensions,
  selectStagedWidgetDimensions,
} from '../../redux/slices/update-width-height-of-chart';
import { removeWidgetAiInsight, selectWidgetAiInsight } from '../../redux/slices/widgetChart/WidgetChartSlice';
import type { DragulaLike } from '../../types/dragula-like';
import { useIsEditableMode } from '../Charts/hooks/useIsEditableMode';
import { useEditSaveDiscard } from '../Charts/hooks/useSaveEdit';
import { useUpdatedTabsOrder } from '../Charts/hooks/useUpdatedTabsOrder';
import WidgetAiInsightPanel from '../common/widget-ai-insight-panel.component';
import DashboardTabView from '../DashboardTabView/dashboard-tab-view';
import { getLayoutsNewPosition } from './layout-tab.functions';
import { TabContent } from './tab-content.component';
import { TabViewHeaderElement } from './tab-header-element.component';

const LayoutTab = () => {
  const [localLayouts, setLocalLayout] = useState<Layout[]>([]);
  const [originalTabPositions, setOriginalTabsPosition] = useState<Array<LayoutWeight>>();
  const [updatedTabPositions, setUpdatedTabPositions] = useState<Array<LayoutWeight>>();
  const [tabInEditMode, setTabinEditMode] = useState(false);

  const dispatch = useAppDispatch();
  const [updateLayout] = useUpdateLayoutMutation();
  const [updateLayoutWidgetData] = useUpdateLayoutWidgetDataMutation();
  const [deleteLayout] = useDeleteLayoutMutation();
  const isEditable = useIsEditableMode();
  const activeTabId = useSelector(selectActiveTabId);
  const stagedWidgetDimensions = useSelector(selectStagedWidgetDimensions);
  const widgetAiInsight = useSelector(selectWidgetAiInsight);
  const saveChangesEdit = useEditSaveDiscard();
  const globalTabReorder = useUpdatedTabsOrder();
  const tabsReference = useRef<TabViewRef>(null);
  const activeTabIdRef = useRef<string>(activeTabId);

  const drakeRef = useRef<DragulaLike | undefined>(undefined);
  const headerId = useRef<string>('mainTabsLayout');
  const isAdminLayout = useLayoutAdmin();

  const { data: layouts, isFetching } = useGetLayoutsQuery({});
  const {
    data: widgets,
    refetch: refetchWidgets,
    isFetching: refetchingWidgets,
  } = useGetLayoutWidgetQuery(activeTabId, {
    skip: !activeTabId,
  });

  const activeTabIndex: number = useMemo(() => {
    // return -1 if layouts is undefined
    return layouts?.findIndex((layout) => layout.id === activeTabId) ?? -1;
  }, [activeTabId, layouts]);

  useEffect(() => {
    drakeRef.current = dragula([], {
      direction: 'horizontal',
      invalid: (el?: Element) => {
        return el?.classList.contains('noDrop') || false;
      },
      accepts(el: Element, target: Element, source: Element, sibling: Element | null) {
        const draggableElement = el?.getElementsByClassName('tabTitle')[0];
        const dropTarget = sibling?.getElementsByClassName('tabTitle')[0];
        // sibling is null, if moved to last position
        // we can move there if all layouts are private or a private layout is in question
        if (!dropTarget) {
          return (
            draggableElement?.classList.contains('userLayout') ??
            target?.getElementsByClassName('publicLayout').length === 0
          );
        }

        const draggableElementClasses = draggableElement?.classList;
        const dropTargetClasses = dropTarget.classList;
        let isPrivateBeforePublic = false;
        let isPublicAfterPrivate = false;
        if (dropTargetClasses && draggableElementClasses) {
          isPrivateBeforePublic =
            draggableElementClasses.contains('userLayout') && dropTargetClasses.contains('publicLayout');
          isPublicAfterPrivate =
            draggableElementClasses.contains('publicLayout') && dropTargetClasses.contains('userLayout');
        }
        return !(isPrivateBeforePublic || isPublicAfterPrivate);
      },
    });
    drakeRef.current?.on('drop', () => {
      const childNodes = drakeRef.current?.containers[0];
      if (childNodes) {
        setUpdatedTabPositions(getLayoutsNewPosition(childNodes.children));
      }
    });
    return () => {
      drakeRef.current?.destroy();
    };
  }, []);

  const setTabsPosition = useCallback(() => {
    if (!layouts) return;

    let publicLayoutCounter = 0;
    let privateLayoutCounter = 0;

    setOriginalTabsPosition(
      layouts.map((layout) => {
        return {
          weight: layout.isUserLayout ? ++publicLayoutCounter : ++privateLayoutCounter,
          id: layout.id,
        };
      })
    );
  }, [layouts]);

  useEffect(() => {
    if (originalTabPositions && updatedTabPositions) {
      dispatch(changedLayoutsWeight(differenceWith(updatedTabPositions, originalTabPositions, isEqual)));
    }
  }, [originalTabPositions, updatedTabPositions, dispatch]);

  useEffect(() => {
    if (widgets && !refetchingWidgets) {
      dispatch(setLayoutWidgets(widgets));
    }
  }, [widgets, refetchingWidgets, dispatch]);

  useEffect(() => {
    if (layouts && !isEmpty(layouts)) {
      dispatch(setActiveLayoutTabType('public'));
      dispatch(setLayoutTabs(layouts));
      setLocalLayout(layouts);

      setTabsPosition();

      // Set default active layout tab if none is set
      if (!activeTabId) {
        dispatch(setActiveLayoutTabId(layouts[0].id));
        tabsReference.current?.setActiveTab(0);
      }
    }
  }, [layouts, dispatch, activeTabId, setTabsPosition]);

  useEffect(() => {
    if (activeTabIndex !== -1) {
      // refetch widgets when active tab changes
      refetchWidgets();
    }
    if (tabsReference.current) {
      tabsReference.current?.setActiveTab(activeTabIndex && activeTabIndex >= 0 ? activeTabIndex : 0);
    }
  }, [activeTabIndex, refetchWidgets]);

  const assignChildDraggableContainer = useCallback(
    (
      editEnabled: boolean,
      drakeRef: React.MutableRefObject<DragulaLike | undefined>,
      childContainer: HTMLElement | null
    ) => {
      if (drakeRef.current) {
        if (childContainer && editEnabled) {
          drakeRef.current.containers.length = 0;
          drakeRef.current.containers.push(childContainer);
          childContainer.style.cursor = 'all-scroll';
        } else {
          drakeRef.current.containers.length = 0;
          drakeRef.current.containers = [];
          if (childContainer) {
            childContainer.style.cursor = 'context-menu';
          }
        }
      }
    },
    []
  );

  const findHeaderRow = useCallback(() => {
    const innerHtmlRow = document.getElementById(headerId.current)?.getElementsByClassName('p-tabview-nav');
    return innerHtmlRow && innerHtmlRow.length > 0 ? (innerHtmlRow[0] as HTMLElement) : null;
  }, []);

  useEffect(() => {
    const header = findHeaderRow();
    if (header) {
      const editEnabled: boolean = isEditable && !tabInEditMode;
      assignChildDraggableContainer(editEnabled, drakeRef, header);
    }
  }, [isEditable, tabInEditMode, assignChildDraggableContainer, findHeaderRow]);

  useEffect(() => {
    if (saveChangesEdit === undefined) {
      return;
    }

    if (!saveChangesEdit) {
      if (!isEmpty(globalTabReorder)) {
        dispatch(discardNewOrder());
        resetChildrenPosition(findHeaderRow());
      }
      if (!isEmpty(stagedWidgetDimensions)) {
        dispatch(clearStagedWidgetDimensions());
      }
      return;
    }

    if (!isEmpty(globalTabReorder)) {
      globalTabReorder.forEach((newOrder) => updateLayout({ ...newOrder, keepOldTags: true }));
    }

    if (!isEmpty(stagedWidgetDimensions)) {
      const entries = Object.entries(stagedWidgetDimensions);
      Promise.allSettled(
        entries.map(([id, dimensions]) =>
          updateLayoutWidgetData({
            id,
            ...dimensions,
          }).unwrap()
        )
      ).then((results) => {
        // Only apply dimensions that were successfully saved so that failed
        // updates revert immediately instead of flickering on the next refetch.
        if (widgets) {
          const savedDimensions = new Map<string, (typeof entries)[number][1]>();
          results.forEach((result, index) => {
            if (result.status === 'fulfilled') {
              const [id, dimensions] = entries[index];
              savedDimensions.set(id, dimensions);
            }
          });

          dispatch(
            setLayoutWidgets(
              widgets.map((w) => {
                const saved = savedDimensions.get(w.id);
                return saved ? { ...w, ...saved } : w;
              })
            )
          );
        }
        dispatch(clearStagedWidgetDimensions());
      });
    }
  }, [
    saveChangesEdit,
    globalTabReorder,
    stagedWidgetDimensions,
    dispatch,
    findHeaderRow,
    updateLayout,
    updateLayoutWidgetData,
  ]);

  const handleSelect = useCallback(
    (index: number) => {
      const layout = layouts && layouts[index];
      if (layout) {
        dispatch(setActiveLayoutTabId(layout.id));
        activeTabIdRef.current = layout.id;
        dispatch(setActiveLayoutTabType(isAdminLayout ? 'admin' : layout.isUserLayout ? 'private' : 'public'));
      }

      dispatch(removeWidgetAiInsight());
    },
    [isAdminLayout, layouts, dispatch]
  );

  const handleUpdateLayout = useCallback(
    (data: Partial<Layout>, removeLayout: boolean, layout: Layout) => {
      if (removeLayout) {
        deleteLayout({ id: layout.id, keepOldTags: false })
          .unwrap()
          .then(() => {
            if (activeTabIdRef.current === layout.id) {
              dispatch(setActiveLayoutTabId(undefined));
            }
            toast.success(t('deleteLayoutSuccessMsg'));
          })
          .catch(() => {
            toast.error(t('deleteLayoutErrorMsg'));
          });
      } else {
        updateLayout({ ...data, id: layout.id, keepOldTags: false });
      }
    },
    [deleteLayout, dispatch, updateLayout]
  );

  return (
    <div className="flex h-full w-full flex-row gap-4 overflow-hidden">
      <div className="tab-view h-full w-full overflow-hidden">
        {isFetching && (
          <div className="flex h-full w-full items-center justify-center">
            <Loader title="Loading" />
          </div>
        )}
        {!isFetching && (
          <DashboardTabView
            activeIndex={activeTabIndex}
            id={headerId.current}
            isEditing={isEditable}
            onTabChange={handleSelect}
            ref={tabsReference}
          >
            {localLayouts.map((layout, index) => (
              <TabViewPanel
                header={
                  <TabViewHeaderElement
                    layoutId={layout.id}
                    isEditable={(layout.canModify || layout.isUserLayout) && isEditable}
                    layout={layout}
                    setTabInEditMode={setTabinEditMode}
                    updateLayout={(data: Partial<Layout>, removeLayout) =>
                      handleUpdateLayout(data, removeLayout, layout)
                    }
                    localIndex={index}
                  />
                }
                key={layout.id}
              >
                <TabContent
                  layout={layout}
                  isEditable={layout.canModify && isEditable}
                  assignChildDraggableContainer={assignChildDraggableContainer}
                />
              </TabViewPanel>
            ))}
          </DashboardTabView>
        )}
      </div>

      <WidgetAiInsightPanel widgetAiInsight={widgetAiInsight} />
    </div>
  );
};

export default LayoutTab;
